<template>
  <q-layout view="hHh LpR lFf" class="bg-main">
    <alzura-nav-bar v-if="$app.whitelabel === 'alzura'" :key="navbarKey" />
    <layout-toolbar />
    <layout-drawer />
    <q-page-container :key="currentBranch.ID">
      <transition name="component-fade" mode="out-in">
        <router-view />
      </transition>
    </q-page-container>
    <!--layout-footer
      v-if="
        $store.getters['auth/alSupport'] &&
        !$store.getters['auth/isEmployee']
      "
    /-->
    <a-welcome-modal :opened.sync="openWelcomeWindow" v-if="openWelcomeWindow" />
    <notifications-dialog
      v-if="openNotificationsDialog"
      :open="openNotificationsDialog"
      :notification="notifications[0]"
    />

    <email-reconnect-modal :isOpen="openEmailModal" v-if="openEmailModal" />

    <forced-logout-modal
      v-if="$app.whitelabel === 'alzura'"
      @account-switched="reloadAlzuraNavbar"
    />
  </q-layout>
</template>

<script>
import LayoutToolbar from './layout-components/Toolbar';
import LayoutDrawer from './layout-components/drawer/DrawerWrapper';
import AWelcomeModal from '@/components/dashboard/onboarding/Onboarding';
import NotificationsDialog from '@/components/base/NotificationsDialog';
import EmailReconnectModal from '@/components/email/EmailReconnectModal.vue';
import ForcedLogoutModal from '@/components/base/ForcedLogoutModal.vue';
import { setAlzuraHeaderHeight } from '@/utils/alzuraNavbarHeight';
import { holdRequestsUntil } from '@/plugins/axios';

import { mapGetters, mapState } from 'vuex';

export default {

  name: 'Default',
  components: {
    LayoutToolbar,
    LayoutDrawer,
    AWelcomeModal,
    NotificationsDialog,
    EmailReconnectModal,
    ForcedLogoutModal,
  },
  data() {
    return {
      miniState: false,
      leftDrawerOpen: true,
      miniWidth: 60,
      width: 200,
      openWelcomeWindow: false,
      navbarResizeObserver: null,
      ssoCheckInterval: null,
      navbarKey: 0,
    };
  },
  computed: {
    ...mapState('auth', ['currentBranch']),
    ...mapState('connection', ['desync']),
    ...mapGetters('auth', [
      'alSupport',
      'isTrialPeriodActive',
      'forcedLogoutReason',
    ]),
    ...mapGetters('subscription', [
      'notSubscriptedToMainPackage',
    ]),
    ...mapState('notifications', ['notifications']),
    openNotificationsDialog() {
      if (this.notifications.length === 0) return false;
      if (this.notifications.some(n => n.showPopUp === 1)) return true;

      return false;
    },
    forwardToPaymentPage() {
      if (this.alSupport) return false;
      if (this.isTrialPeriodActive) return false;
      return this.notSubscriptedToMainPackage;
    },
    openEmailModal() {
      return this.currentBranch.nylasGrantStatus === 'invalid';
    },
  },
  methods: {
    async checkSSOCookie() {
      if (this.forcedLogoutReason) return; // modal is already showing, nothing more to detect

      const base = import.meta.env.PROD ? 'https://autengo.alzura.com' : '';
      try {
        const { data } = await this.$axios.get(`${base}/v2/auth/alzura/sso/identity`, {
          withCredentials: true,
          errorNotification: false,
        });
        if (!data.match) {
          console.warn(`SSO identity check failed (${data.reason}), showing forced logout message`);
          this.$store.commit('auth/SET_FORCED_LOGOUT_REASON', data.reason);
        }
      } catch (err) {
        console.warn('SSO identity check could not be performed', err);
      }
    },
    onVisibilityChange() {
      if (document.visibilityState === 'visible') {
        this.checkSSOCookie();
      }
    },
    async loadAlzuraNavbar() {
      try {
        const { data } = await this.$store.dispatch('auth/getAlzuraNavbar');
        if (!document.querySelector('script[data-navbar]')) {
          const script = document.createElement('script');
          script.src = data.url;
          script.async = true;
          script.dataset.navbar = 'true';

          script.onload = () => {
            const navbar = document.querySelector('alzura-nav-bar');

            if (navbar) {
              const updateLayout = () => {
                const navbarHeight = navbar.getBoundingClientRect().height;

                const header = document.querySelector('.a-layout-header');
                if (header) {
                  // margin (not padding) so the toolbar's own box/padding stays untouched
                  header.style.marginTop = `${navbarHeight}px`;

                  // Overall header stack = alzura navbar + our own toolbar's real height
                  const toolbarHeight = header.getBoundingClientRect().height;
                  setAlzuraHeaderHeight(navbarHeight + toolbarHeight);
                }

                const drawer = document.querySelector('.q-layout-drawer');
                if (drawer) {
                  drawer.style.paddingTop = `${navbarHeight}px`;
                }

                const layout = document.querySelector('.q-layout');
                if (layout) {
                  layout.style.paddingTop = `${navbarHeight}px`;
                }
              };
              updateLayout();

              this.navbarResizeObserver = new ResizeObserver(() => {
                updateLayout();
              });
              this.navbarResizeObserver.observe(navbar);
            }
          };

          document.body.appendChild(script);
        }
      } catch (err) {
        console.warn('Failed to load alzura navbar', err);
      }
    },
    async reloadAlzuraNavbar() {
      this.navbarResizeObserver?.disconnect();
      document.querySelector('script[data-navbar]')?.remove();
      this.navbarKey += 1;
      await this.$nextTick();
      await this.loadAlzuraNavbar();
    },
  },
  watch: {
    forwardToPaymentPage: {
      immediate: true,
      handler(forwardToPaymentPage) {
        if (forwardToPaymentPage) this.$router.replace('/payment');
      },
    },
    desync: {
      immediate: true,
      async handler(desync) {
        if (!desync) return;

        await new Promise(resolve => setTimeout(resolve, 1000));

        this.$q.notify({
          message: 'Eine neue Version wurde veröffentlicht!',
          detail: 'Bitte laden Sie die Seite neu.',
          type: 'info',
          position: 'bottom-right',
          timeout: 0,
          actions: [
            {
              icon: 'refresh',
              handler: () => {
                window.location.reload();
              },
            },
          ],
        });
      },
    },
  },
  async created() {
    if (this.$app.whitelabel === 'alzura') {
      this.$options.ssoCheck = this.checkSSOCookie();
      holdRequestsUntil(this.$options.ssoCheck);
    }

    this.$store.dispatch('userSetting/getAllSettings');
    this.$store.dispatch('auth/reloadCurrentBranch');
    this.$store.dispatch('notifications/getAllNotifications');
    this.openWelcomeWindow = await this.$store.dispatch('branch/shouldShowWelcomeWindow');
  },
  async mounted() {
    if (this.$app.whitelabel === 'alzura') {
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      this.ssoCheckInterval = setInterval(this.checkSSOCookie, 2 * 60 * 1000);
      // Don't load the navbar if the user is not logged in anymore
      await this.$options.ssoCheck;
      if (!this.forcedLogoutReason) await this.loadAlzuraNavbar();
    }
  },
  beforeDestroy() {
    if (this.$app.whitelabel === 'alzura') {
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      this.navbarResizeObserver?.disconnect();
      clearInterval(this.ssoCheckInterval);
    }
  },
};
</script>

<style scoped lang="stylus">
.component-fade-enter-active, .component-fade-leave-active {
  transition: opacity 0.3s ease;
}

.component-fade-enter, .component-fade-leave-to {
  opacity: 0;
}
</style>

<style>
alzura-nav-bar {
    position: fixed;
    left: 0;
    right: 0;
    top: 0;
    z-index: 2407;
}
</style>
